import { z } from 'zod';

import { Action, AssignmentDefinition, DataCollection, EntityKind, StepDefinitionId } from '@willow/types-iso';

import { Expression } from '../../Expressions/AbstractExpressionEvaluator';
import { CreateTask } from '../traits/CreateTask';
import { DueDefinition } from '../traits/DueDefinition';
import { RetryDefinition } from '../traits/RetryDefinition';
import { OnActivateAction, OnCompleteAction } from './OnEventAction';

/**
 * Step definitions will roll up into a workflow definition somehow
 *
 * TODO think about composition of step definitions -- we'll need relationships
 * between them because e.g. a call campaign and a letter-writing campaign may
 * roll up into the same "resolve the delinqunency"-type task and the completion
 * of that task nullify the other tasks
 */
export const BaseStepDefinition = z.object({
  id: StepDefinitionId.meta({
    description: 'The ID of the step definition.',
    example: 'c0ef58c7-4f5c-4352-a775-2a75ea4c6d88',
    type: 'string',
  }),
  title: z.string(),
  description: z.string().optional(),

  /**
   * Information to be displayed above the form
   */
  displayInfo: z
    .array(
      z.object({
        markdownContent: z.string(),
        isVisible: z.union([Expression('boolean'), z.boolean()]),
      }),
    )
    .optional(),

  due: DueDefinition,
  retry: RetryDefinition,

  // TODO this will probably need to be a more complex object
  createTask: CreateTask.optional(),

  assignment: AssignmentDefinition,
  dataCollection: DataCollection.optional(),

  actionsToTake: z.array(
    z.object({
      action: Action,
      if: z.union([Expression('boolean'), z.boolean()]),
    }),
  ),

  onActivateActions: z.array(OnActivateAction).optional(),
  onCompleteActions: z.array(OnCompleteAction).optional(),

  /**
   * Declares branded entity IDs to pre-allocate on this step's first
   * activation. The engine generates a UUID per entry, brands it according to
   * `kind`, and writes it into the step instance's `preallocatedEntityRefs`
   * map. Expressions read those IDs via `preallocatedEntityRef(stepId, key)`
   * and predicates like `loanHasAppraisalWithId(id)` then check whether the
   * entity has landed on the loan snapshot.
   */
  preallocatedEntityRefs: z.record(z.string(), z.object({ kind: EntityKind })).optional(),

  // had considered making these optional but it's easier to think about for now
  // if we have to explicitly consider each
  automaticallyActivate: Expression('boolean'),
  automaticallySkip: Expression('boolean'),
  automaticallyComplete: Expression('boolean'),
  automaticallyFail: Expression('boolean'),
  canManuallyActivate: Expression('boolean'),
  canManuallySkip: Expression('boolean'),
  canManuallyComplete: Expression('boolean'),
  canManuallyFail: Expression('boolean'),

  // if omitted, the step is always visible
  isVisible: Expression('boolean').optional(),

  // if omitted, assume false
  supportNotes: z.boolean().optional(),

  /**
   * Whether this step can be re-activated for editing after completion (or skip).
   * If omitted, defaults to false (step cannot be edited after resolution).
   */
  canEditAfterCompletion: Expression('boolean').optional(),
});

export type BaseStepDefinition = z.infer<typeof BaseStepDefinition>;
